import type { ResolvedMonospaceDataSource } from '#layers/data-model/types/schema/resolved-schema';
import { useI18n } from 'vue-i18n';
import { useWorkspaceRuntimeHandle } from '#layers/runtime/public';
import { useToastResultHandling } from '#layers/ui/public';
import { useWorkspaceSchemaStore } from '#layers/workspace/public';

/**
 * Deletes a data source and refreshes the workspace schema, surfacing success or
 * failure as a toast. Shared by the data-sources list and the onboarding step.
 */
export function useRemoveDataSource() {
	const { t } = useI18n();
	const { refresh: refreshSchema } = useWorkspaceSchemaStore();
	const { onSuccess, onError } = useToastResultHandling({
		success: { title: t('pages.data_model.remove_source_success.title') },
		error: { title: t('pages.data_model.remove_source_error.title') },
	});

	async function removeDataSource(source: ResolvedMonospaceDataSource) {
		const handle = useWorkspaceRuntimeHandle();
		const { fetch } = handle;

		return handle.run(async () => {
			try {
				await fetch(`sources/data/${source.id}`, { method: 'DELETE' });

				if (handle.isActive()) {
					onSuccess();
					await refreshSchema();
				}
			}
			catch (error) {
				if (handle.isActive()) onError(error);
			}
		});
	}

	return { removeDataSource };
}
